import { ActivityText } from "@components/ActivityText/ActivityText";
import { ArrowBack } from "@mui/icons-material";
import { NavigationButton, TaskError } from "@components";
import { useActivityFlowContext } from "@context/ActivityFlowContext";
import { formatLabel } from "@utils/formatLabel";
import { Box, List, ListItem, Typography } from "@mui/material";
import { theme } from "@theme";

const renderParagraph = (paragraph: string) => {
  const separatorIndex = paragraph.indexOf(":");

  if (separatorIndex === -1) {
    return <ActivityText key={paragraph}>{paragraph}</ActivityText>;
  }

  return (
    <Typography
      key={paragraph}
      component="div"
      sx={{
        fontSize: "0.9rem",
        lineHeight: 1.45,
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
        }}
      >
        <ActivityText>{paragraph.slice(0, separatorIndex + 1)}</ActivityText>
      </Typography>
      <ActivityText>{paragraph.slice(separatorIndex + 1)}</ActivityText>
    </Typography>
  );
};

export const TaskView = () => {
  const {
    selectedActivity: activity,
    completeActivity,
    returnToTaskSelection,
    goToDashboard,
  } = useActivityFlowContext();

  if (!activity) {
    return TaskError(goToDashboard);
  }

  const toolName = formatLabel(activity.tool).replace(" Ai", "");

  return (
    <Box
      sx={{
        padding: 4,
      }}
    >
      <Typography
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          fontSize: "0.75rem",
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 700,
          }}
        >
          YOUR CHALLENGE
        </Typography>
        <Typography aria-hidden="true">•</Typography>
        {toolName} Environment
      </Typography>
      <Typography variant="h1">{activity.title}</Typography>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          marginTop: 2,
        }}
      >
        {activity.content.paragraphs.map((paragraph) => {
          return renderParagraph(paragraph);
        })}
      </Box>

      <Typography
        sx={{
          fontSize: "0.9rem",
          marginTop: 1,
        }}
      >
        Read through the steps below, then open {toolName} and begin. When
        you’re done, come back here to reflect on your work.
      </Typography>

      <List
        sx={{
          display: "grid",
          gap: 1.5,
          marginTop: 2.75,
          padding: 0,
        }}
      >
        {activity.content.steps.map((step, index) => (
          <ListItem
            key={step}
            sx={{
              gridTemplateColumns: "auto 1fr",
              gap: 2,
              padding: 2,
              border: `1px solid ${theme.palette.info.main}`,
              borderRadius: 0.8,
              background: theme.palette.info.light,
              fontSize: "0.875rem",
            }}
          >
            <Typography
              sx={{
                display: "grid",
                minWidth: 30,
                height: 30,
                borderRadius: "50%",
                placeItems: "center",
                color: "white",
                background: theme.palette.primary.main,
                fontSize: "0.7rem",
                fontWeight: 800,
              }}
              aria-hidden="true"
            >
              {String(index + 1).padStart(2, "0")}
            </Typography>
            <Typography
              component="div"
              sx={{
                "& > p:first-of-type": { marginTop: 0 },
                "& > :last-child": { marginBottom: 0 },
              }}
            >
              <ActivityText block>{step}</ActivityText>
            </Typography>
          </ListItem>
        ))}
      </List>

      {activity.content.verification && (
        <Box
          sx={{
            marginTop: 1,
            padding: 2.5,
            borderRadius: 1,
            background: theme.palette.secondary.light,
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>
            How to check your work
          </Typography>
          <Typography
            sx={{
              fontSize: "0.875rem",
            }}
          >
            <ActivityText>{activity.content.verification}</ActivityText>
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          justifyContent: "space-between",
          marginTop: 3,
        }}
      >
        <NavigationButton
          variant="outlined"
          startIcon={<ArrowBack />}
          type="button"
          onClick={returnToTaskSelection}
        >
          Back
        </NavigationButton>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <NavigationButton
            href={activity.content.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in {toolName} &amp; Start
          </NavigationButton>
          <NavigationButton onClick={completeActivity}>
            Mark as complete
          </NavigationButton>
        </Box>
      </Box>
    </Box>
  );
};
